import { useState, useRef } from "react";
import { getToken } from "../../domain/loginSlice.js";
import PageTitle from "../PageTitle.js";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Snackbar,
  IconButton,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";

const SERVER_URL = import.meta.env.VITE_SERVER_API_URL;

const REQUIRED_COLUMNS = [
  "barcode",
  "boardId",
  "columns",
  "depth",
  "layers",
  "shape",
  "size",
  "is_amorphic",
  "x_start_pr",
  "x_end_pr",
];

function UploadPlanogram() {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  const validateHeaders = (headers) => {
    const missingColumns = REQUIRED_COLUMNS.filter(
      (col) =>
        !headers.map((h) => h.trim().toLowerCase()).includes(col.toLowerCase())
    );

    if (missingColumns.length > 0) {
      throw new Error(`Missing required columns: ${missingColumns.join(", ")}`);
    }
  };

  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleFileChange = async (event) => {
    try {
      setNotification({ open: false, message: "", severity: "success" });
      setValidationError(null);
      setPreviews([]);
      const selectedFiles = Array.from(event.target.files);

      if (
        selectedFiles.some((file) => !file.name.toLowerCase().endsWith(".csv"))
      ) {
        setValidationError("Please upload only CSV files");
        setFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      setFiles(selectedFiles);

      const newPreviews = await Promise.all(
        selectedFiles.map(
          (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (e) => {
                try {
                  const content = e.target.result;
                  const lines = content
                    .split("\n")
                    .filter((line) => line.trim());

                  if (lines.length < 2) {
                    throw new Error("CSV file is empty or contains no data");
                  }

                  const headers = lines[0]
                    .split(",")
                    .map((header) => header.trim());
                  validateHeaders(headers);

                  const data = lines
                    .slice(1, 6)
                    .map((line) => line.split(",").map((cell) => cell.trim()));

                  resolve({ fileName: file.name, headers, data });
                } catch (error) {
                  reject(error);
                }
              };

              reader.onerror = () => reject(new Error("Error reading file"));
              reader.readAsText(file);
            })
        )
      );

      setPreviews(newPreviews);
    } catch (error) {
      setValidationError(error.message);
      setFiles([]);
      setPreviews([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    try {
      setLoading(true);
      setNotification({ open: false, message: "", severity: "success" });

      const formData = new FormData();
      files.forEach((file) => formData.append("files", file)); // Append multiple files

      const response = await fetch(`${SERVER_URL}/api/field/upload-planogram`, {
        method: "POST",
        body: formData,
        headers: {
          authorization: getToken("accessToken"),
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || "Upload failed");
      }

      showNotification(`Files uploaded successfully!`);
      handleReset();
    } catch (error) {
      showNotification(error.message || "An error occurred during upload", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFiles([]);
    setPreviews([]);
    setValidationError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <div style={{ height: "100%", overflowY: "scroll", padding: 20 }}>
      <PageTitle>Add Planogram</PageTitle>

      <Box sx={{ margin: "auto", padding: 2 }}>
        {validationError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <IconButton size="small" onClick={() => setValidationError(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {validationError}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <input
            ref={fileInputRef}
            accept=".csv"
            style={{ display: "none" }}
            id="raised-button-file"
            type="file"
            name="files" // This must match the backend field name
            multiple
            onChange={handleFileChange}
            onClick={(e) => {
              e.target.value = "";
            }}
          />
          <label htmlFor="raised-button-file">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={loading}
            >
              Select CSV Files
            </Button>
          </label>

          {files.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleReset}
              disabled={loading}
            >
              Clear
            </Button>
          )}
        </Box>

        {files.length > 0 && (
          <Typography variant="body1" sx={{ mt: 2 }}>
            Selected files: {files.map((file) => file.name).join(", ")}
          </Typography>
        )}

        {previews.map((preview, index) => (
          <Box key={index} sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Preview of {preview.fileName} (first 5 rows):
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {preview.headers.map((header, index) => (
                      <TableCell key={index} sx={{ fontWeight: "bold" }}>
                        {header}
                        {REQUIRED_COLUMNS.includes(header.toLowerCase()) && (
                          <span style={{ color: "red" }}> *</span>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.data.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}

        <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={files.length === 0 || loading || validationError}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? "Uploading..." : "Upload CSVs"}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default UploadPlanogram;
